/**
 * Bat Cave Upload Portal
 *
 * Self-service upload portal for transitioning loan officers to securely transfer documents.
 * No authentication required - public access with recipient selection.
 */

import { useEffect, useState, useRef } from 'react';
import {
  Upload, Loader2, FileText, CheckCircle2, X,
  AlertCircle, Shield, Send, Users, Briefcase, ChevronDown, Check
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import {
  getPortalData,
  uploadDocument,
  submitPackage,
  formatFileSize
} from '@/services/batcaveService';
import type { BatCaveUploadFile, BatCaveUploaderInfo } from '@/types/batcave';

interface Recipient {
  id: string;
  name: string;
  title?: string;
}

interface CompanyInfo {
  name: string;
  email?: string;
  logoUrl?: string;
  primaryColor?: string;
}

export const BatCaveUploadPortal = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [company, setCompany] = useState<CompanyInfo>({ name: 'Lendwise Mortgage' });
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>([]);
  const [recipientPopoverOpen, setRecipientPopoverOpen] = useState(false);
  const [packageId, setPackageId] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<BatCaveUploadFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [uploaderInfo, setUploaderInfo] = useState<BatCaveUploaderInfo>({
    name: '',
    email: '',
    phone: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const packageIdRef = useRef<string | null>(null);

  useEffect(() => {
    loadPortalData();
  }, []);

  const loadPortalData = async () => {
    try {
      const data = await getPortalData();
      setRecipients(data.recipients);
      setCompany(data.company);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portal');
    } finally {
      setLoading(false);
    }
  };

  const toggleRecipient = (recipientId: string) => {
    setSelectedRecipientIds(prev =>
      prev.includes(recipientId)
        ? prev.filter(id => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const handleUploadFile = async (file: File) => {
    const fileId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const newFile: BatCaveUploadFile = {
      id: fileId,
      file,
      progress: 0,
      status: 'uploading'
    };

    setUploadedFiles(prev => [...prev, newFile]);

    try {
      // Use ref for immediate access (avoids race condition with parallel uploads)
      const result = await uploadDocument(
        file,
        packageIdRef.current || packageId || undefined,
        (progress) => {
          setUploadedFiles(prev =>
            prev.map(f => f.id === fileId ? { ...f, progress } : f)
          );
        }
      );

      // Store packageId in ref immediately (synchronous) AND state (for UI)
      if (result.packageId && !packageIdRef.current) {
        packageIdRef.current = result.packageId;
        setPackageId(result.packageId);
      }

      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, status: 'success', documentId: result.documentId, progress: 100 }
            : f
        )
      );

      toast.success(`${file.name} uploaded successfully`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';

      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, status: 'error', error: errorMessage }
            : f
        )
      );

      toast.error(errorMessage);
    }
  };

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) {
return;
}

    // If no packageId exists yet, upload first file and wait for it to create the package
    // This prevents race condition where parallel uploads each create their own package
    if (!packageIdRef.current && !packageId) {
      await handleUploadFile(fileArray[0]);
      // Now upload remaining files in parallel (packageIdRef is set)
      fileArray.slice(1).forEach(file => {
        handleUploadFile(file);
      });
    } else {
      // Package already exists, upload all in parallel
      fileArray.forEach(file => {
        handleUploadFile(file);
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    e.target.value = '';
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleComplete = async () => {
    // Validate required fields
    if (!uploaderInfo.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!uploaderInfo.email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    if (!uploaderInfo.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (selectedRecipientIds.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    if (!packageId) {
      toast.error('Please upload at least one file');
      return;
    }

    setIsCompleting(true);
    try {
      await submitPackage(packageId, uploaderInfo, selectedRecipientIds);
      setIsComplete(true);
      toast.success('Documents submitted! Recipients have been notified.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to complete upload');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleUploadMore = () => {
    // Reset for another upload session
    setIsComplete(false);
    packageIdRef.current = null;
    setPackageId(null);
    setUploadedFiles([]);
    setSelectedRecipientIds([]);
    setUploaderInfo({ name: '', email: '', phone: '' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-slate-300">Accessing the Bat Cave...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-400" />
            </div>
            <CardTitle className="text-xl text-white">Unable to Load Portal</CardTitle>
            <CardDescription className="text-slate-400 mt-2">{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button
              variant="outline"
              onClick={() => {
                setError(null);
                setLoading(true);
                loadPortalData();
              }}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success/complete state
  if (isComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
            <CardTitle className="text-xl text-white">Transfer Complete!</CardTitle>
            <CardDescription className="text-slate-400 mt-2">
              Your documents have been securely transferred. The team has been notified.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-700/50 rounded-lg p-4 text-center">
              <p className="text-sm text-slate-400 mb-3">Need to start another upload?</p>
              <Button
                variant="outline"
                onClick={handleUploadMore}
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              >
                Start New Transfer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const successfulUploads = uploadedFiles.filter(f => f.status === 'success').length;
  const isUploading = uploadedFiles.some(f => f.status === 'uploading');
  const hasValidInfo = uploaderInfo.name.trim() && uploaderInfo.email.trim() && uploaderInfo.phone.trim();
  // Filter out recipients with no name
  const validRecipients = recipients.filter(r => r.name && r.name.trim() && r.name !== 'null null');
  const selectedRecipients = validRecipients.filter(r => selectedRecipientIds.includes(r.id));

  return (
    <div className="h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <header className="bg-slate-800/80 backdrop-blur border-b border-slate-700 py-4 px-6 shrink-0">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          {company.logoUrl ? (
            <img src={company.logoUrl} alt={company.name} className="h-10 object-contain" />
          ) : (
            <span className="text-xl font-bold text-white">{company.name}</span>
          )}
          <div className="flex items-center gap-2 text-sm text-amber-400">
            <Shield className="h-4 w-4" />
            <span>Bat Cave</span>
          </div>
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto min-h-0">
        <div className="max-w-3xl mx-auto p-6 md:p-8 pb-8">
          <Card className="shadow-2xl bg-slate-800 border-slate-700">
            <CardHeader className="border-b border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="h-7 w-7 text-white" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-xl text-white">Secure Document Transfer</CardTitle>
                  <CardDescription className="text-slate-400 mt-1">
                    Upload your transition documents securely. Select who should receive them.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-6 pt-6">
              {/* Uploader Info Form */}
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  Your Information
                  <span className="text-amber-400 text-xs font-normal">(Required)</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-300">Name</Label>
                    <Input
                      id="name"
                      placeholder="Your full name"
                      value={uploaderInfo.name}
                      onChange={(e) => setUploaderInfo(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-slate-600 border-slate-500 text-white placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={uploaderInfo.email}
                      onChange={(e) => setUploaderInfo(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-slate-600 border-slate-500 text-white placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-300">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={uploaderInfo.phone}
                      onChange={(e) => setUploaderInfo(prev => ({ ...prev, phone: e.target.value }))}
                      className="bg-slate-600 border-slate-500 text-white placeholder:text-slate-400"
                    />
                  </div>
                </div>
              </div>

              {/* Recipient Selection */}
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Users className="h-4 w-4 text-amber-400" />
                  Who should receive these documents?
                  <span className="text-amber-400 text-xs font-normal">(Required)</span>
                </h3>

                <Popover open={recipientPopoverOpen} onOpenChange={setRecipientPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={recipientPopoverOpen}
                      className="w-full justify-between bg-slate-600 border-slate-500 text-white hover:bg-slate-500 hover:text-white"
                    >
                      {selectedRecipientIds.length === 0 ? (
                        <span className="text-slate-400">Select recipients...</span>
                      ) : (
                        <span>{selectedRecipientIds.length} recipient{selectedRecipientIds.length !== 1 ? 's' : ''} selected</span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0 bg-slate-700 border-slate-600" align="start">
                    <Command className="bg-slate-700">
                      <CommandInput placeholder="Search team members..." className="text-white" />
                      <CommandList>
                        <CommandEmpty className="text-slate-400 p-4 text-center">No recipients found.</CommandEmpty>
                        <CommandGroup>
                          {validRecipients.map((recipient) => {
                            const isSelected = selectedRecipientIds.includes(recipient.id);
                            return (
                              <CommandItem
                                key={recipient.id}
                                value={recipient.name}
                                onSelect={() => toggleRecipient(recipient.id)}
                                className="text-white hover:bg-slate-600 cursor-pointer"
                              >
                                <div className={cn(
                                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                                  isSelected
                                    ? "bg-amber-500 border-amber-500 text-white"
                                    : "border-slate-500"
                                )}>
                                  {isSelected && <Check className="h-3 w-3" />}
                                </div>
                                <div className="flex flex-col">
                                  <span>{recipient.name}</span>
                                  {recipient.title && (
                                    <span className="text-xs text-slate-400">{recipient.title}</span>
                                  )}
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {/* Selected Recipients Display */}
                {selectedRecipients.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedRecipients.map(recipient => (
                      <Badge
                        key={recipient.id}
                        variant="secondary"
                        className="bg-amber-500/20 text-amber-300 border-amber-500/30 flex items-center gap-1"
                      >
                        {recipient.name}
                        <button
                          onClick={() => toggleRecipient(recipient.id)}
                          className="ml-1 hover:text-white"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Dropzone */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />

                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer group",
                    isDragging && "border-amber-500 bg-amber-500/10 scale-[1.02]",
                    !isDragging && "border-slate-600 bg-slate-700/30 hover:border-amber-400 hover:bg-amber-500/5"
                  )}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className={cn(
                      "w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300",
                      isDragging ? "bg-amber-500 text-white" : "bg-slate-600 text-slate-300 group-hover:bg-amber-500 group-hover:text-white"
                    )}>
                      <Upload className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">
                        {isDragging ? 'Drop files here' : 'Drag & drop files or click to browse'}
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        No file size limits - upload anything you need
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <FileText className="h-4 w-4 text-amber-400" />
                    Files ({successfulUploads} uploaded)
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {uploadedFiles.map(file => (
                      <div
                        key={file.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border transition-all",
                          file.status === 'success' && "bg-green-900/20 border-green-800",
                          file.status === 'uploading' && "bg-amber-900/20 border-amber-800",
                          file.status === 'error' && "bg-red-900/20 border-red-800"
                        )}
                      >
                        <div className={cn(
                          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                          file.status === 'success' && "bg-green-500 text-white",
                          file.status === 'uploading' && "bg-amber-500 text-white",
                          file.status === 'error' && "bg-red-500 text-white"
                        )}>
                          {file.status === 'uploading' && <Loader2 className="h-4 w-4 animate-spin" />}
                          {file.status === 'success' && <CheckCircle2 className="h-4 w-4" />}
                          {file.status === 'error' && <AlertCircle className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{file.file.name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span>{formatFileSize(file.file.size)}</span>
                            {file.status === 'uploading' && (
                              <span className="text-amber-400">{file.progress}%</span>
                            )}
                            {file.error && <span className="text-red-400">{file.error}</span>}
                          </div>
                          {file.status === 'uploading' && (
                            <div className="mt-1 h-1 bg-slate-600 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-amber-500 transition-all duration-300"
                                style={{ width: `${file.progress}%` }}
                              />
                            </div>
                          )}
                        </div>
                        {file.status !== 'uploading' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(file.id);
                            }}
                            className="flex-shrink-0 p-1 text-slate-400 hover:text-white rounded"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex-col gap-4 border-t border-slate-700 pt-6">
              <Button
                onClick={handleComplete}
                disabled={successfulUploads === 0 || isUploading || isCompleting || !hasValidInfo || selectedRecipientIds.length === 0}
                className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold"
                size="lg"
              >
                {isCompleting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Completing Transfer...
                  </>
                ) : isUploading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Complete Transfer {successfulUploads > 0 ? `(${successfulUploads} files)` : ''}
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-slate-500">
                Your documents are encrypted and securely transferred.
              </p>
            </CardFooter>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-800/50 border-t border-slate-700 py-4 px-6 shrink-0">
        <div className="max-w-3xl mx-auto text-center text-sm text-slate-500">
          <p>&copy; {new Date().getFullYear()} {company.name}. All rights reserved.</p>
          {company.email && (
            <p className="mt-1">
              Need help? Contact <a href={`mailto:${company.email}`} className="text-amber-400 hover:underline">{company.email}</a>
            </p>
          )}
        </div>
      </footer>
    </div>
  );
};
